import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { ArrowRight } from "lucide-react";

const STORAGE_KEY = "lc_login_remember";

// Light obfuscation only — meant for a single-user personal device.
// Not a security boundary: anyone with access to the browser sees the data.
function encode(obj) {
  try {
    return btoa(unescape(encodeURIComponent(JSON.stringify(obj))));
  } catch {
    return "";
  }
}
function decode(str) {
  try {
    return JSON.parse(decodeURIComponent(escape(atob(str))));
  } catch {
    return null;
  }
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Hydrate from localStorage on mount — intentional empty deps (one-shot).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const saved = decode(raw);
    if (saved?.email) {
      setEmail(saved.email);
      setPassword(saved.password || "");
      setRemember(true);
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      if (remember) {
        localStorage.setItem(STORAGE_KEY, encode({ email: email.trim().toLowerCase(), password }));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Identifiants invalides.");
    } finally {
      setLoading(false);
    }
  };

  const onForget = () => {
    localStorage.removeItem(STORAGE_KEY);
    setEmail("");
    setPassword("");
    setRemember(false);
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground" data-testid="login-page">
      <div className="hidden md:flex md:w-1/2 bg-foreground text-background p-12 lg:p-16 flex-col justify-between">
        <p className="text-eyebrow text-background/60">Luigi Chauveau · Espace gestion</p>
        <div>
          <h1 className="h-display text-5xl lg:text-6xl leading-[1.02] text-balance">
            Le carnet
            <br />
            <span className="italic text-primary">d'atelier</span>.
          </h1>
          <p className="mt-6 text-background/70 text-[14px] max-w-sm leading-relaxed">
            Demandes, clients, devis, factures, réalisations. Tout au même
            endroit, dans le même esprit que le site public.
          </p>
        </div>
        <p className="text-serial text-background/40">— Artisan depuis 2015</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-5 md:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-[420px]" data-testid="login-form" autoComplete="on">
          <p className="text-eyebrow mb-4">Connexion</p>
          <h2 className="h-display text-4xl md:text-5xl mb-12 leading-tight">
            Bonjour,
            <br />
            <span className="italic text-primary">Luigi</span>.
          </h2>

          <div className="space-y-6">
            <div>
              <label className="text-eyebrow block mb-2" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus={!email}
                className="w-full bg-transparent border-b border-border focus:border-primary py-3 text-[15px] outline-none transition-colors"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="text-eyebrow block mb-2" htmlFor="login-password">Mot de passe</label>
              <input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-transparent border-b border-border focus:border-primary py-3 text-[15px] outline-none transition-colors"
                data-testid="login-password"
              />
            </div>
          </div>

          <div className="mt-7 flex items-center justify-between gap-4">
            <label className="flex items-center gap-3 text-[13px] cursor-pointer select-none" data-testid="login-remember-label">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 accent-primary"
                data-testid="login-remember"
              />
              <span>Se souvenir de moi sur cet ordinateur</span>
            </label>
            {(email || password) && (
              <button
                type="button"
                onClick={onForget}
                className="text-[12px] text-muted-foreground link-underline-static"
                data-testid="login-forget"
              >
                Oublier
              </button>
            )}
          </div>

          {error && (
            <p className="text-destructive text-[13px] mt-6" data-testid="login-error">
              {error}
            </p>
          )}

          <button type="submit" className="btn-ink mt-10 w-full justify-center" disabled={loading} data-testid="login-submit">
            {loading ? "Connexion…" : "Se connecter"} <ArrowRight size={14} />
          </button>

          <p className="text-[11px] text-muted-foreground mt-6 leading-relaxed">
            La case « se souvenir » enregistre vos identifiants dans ce
            navigateur uniquement. À n'activer que sur un ordinateur personnel.
          </p>
        </form>
      </div>
    </div>
  );
}
